// 宇宙のひとこと用のデータをまとめて用意するフック（トップの枠と詳細ページで共用）
import React from 'react';
import { getMoonInfo, MoonInfo } from './moon';
import { getSunInfo, SunInfo } from './sun';
import { getVisiblePlanets, getMeteorShower, VisiblePlanet, MeteorInfo } from './sky';
import { getEarthInfo, EarthInfo } from './earth';
import { fetchSpaceWeather, SpaceWeather } from './spaceWeather';
import { getNextIssPass, IssPass } from './iss';
import type { UserLocation } from '../../lib/userLocation';

export type CosmosData = {
  now: Date;
  loc: UserLocation | null | undefined; // undefined=確認中, null=位置なし
  moon: MoonInfo;
  sun: SunInfo;
  earth: EarthInfo;
  planets: VisiblePlanet[];
  meteor: MeteorInfo | null;
  wx: SpaceWeather | null | undefined;  // undefined=取得中
  iss: IssPass | null | undefined;      // undefined=計算中／位置なし
};

/* eslint-disable react-hooks/exhaustive-deps */
export function useCosmosData(loc: UserLocation | null | undefined, dateKey?: string): CosmosData {
  const now = React.useMemo(() => new Date(), [dateKey]);
  const moon = React.useMemo(() => getMoonInfo(now), [now]);
  // 二十四節気は位置に依存しない。日の出・日の入りは loc がある場合だけ意味を持つ
  const sun = React.useMemo(() => getSunInfo(now, loc?.lat ?? 0, loc?.lng ?? 0), [now, loc?.lat, loc?.lng]);
  const earth = React.useMemo(() => getEarthInfo(now, moon.age), [now, moon.age]);
  const planets = React.useMemo(() => (loc ? getVisiblePlanets(new Date(), loc.lat, loc.lng) : []), [now, loc?.lat, loc?.lng]);
  const meteor = React.useMemo(() => getMeteorShower(now), [now]);

  const [wx, setWx] = React.useState<SpaceWeather | null | undefined>(undefined);
  React.useEffect(() => {
    let alive = true;
    fetchSpaceWeather().then((v) => { if (alive) setWx(v); });
    return () => { alive = false; };
  }, [now]);

  const [iss, setIss] = React.useState<IssPass | null | undefined>(undefined);
  React.useEffect(() => {
    if (!loc) { setIss(undefined); return; }
    let alive = true;
    const id = setTimeout(() => {
      getNextIssPass(new Date(), loc.lat, loc.lng).then((p) => { if (alive) setIss(p); }).catch(() => { if (alive) setIss(null); });
    }, 300);
    return () => { alive = false; clearTimeout(id); };
  }, [now, loc?.lat, loc?.lng]);

  return { now, loc, moon, sun, earth, planets, meteor, wx, iss };
}
/* eslint-enable react-hooks/exhaustive-deps */
